(ns app.domain.verdict.section
  (:require #?(:clj  [core.util.edn :as edn]
               :cljs [cljs.reader])
            [app.rich-text :as rich-text]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.db.entity.util :as eu]))

(def static-types
  "Sections that should only appear once (usability feature, nothing about them
  makes anything actually *break*)."
  #{;; top-level types
    :verdict.section.type/attachments
    :verdict.section.type/forms

    ;; subtypes
    :verdict.section.type/inspections
    :verdict.section.type/general-conditions
    :verdict.section.type/permit-specific-conditions
    :verdict.section.type/permit-location
    :verdict.section.type/permit-base-data
    :verdict.section.type/roll-number
    :verdict.section.type/reviewers
    :verdict.section.type/permit-number
    :verdict.section.type/parties
    :verdict.section.type/cover-page-permit-data
    :verdict.section.type/cover-page-reviews
    :verdict.section.type/table-of-contents
    :verdict.section.type/permit-category
    :verdict.section.type/permit-type
    :verdict.section.type/permit-set-label
    :verdict.section.type/permit-information
    :verdict.section.type/fees-and-payments
    :verdict.section.type/permission-granted
    :verdict.section.type/permit-description
    :verdict.section.type/qr-code
    :verdict.section.type/permit-sign-off
    :verdict.section.type/property-attributes
    :verdict.section.type/header
    :verdict.section.type/work-type
    :verdict.section.type/bonds
    #__})

(defn- get-section-id
  "Get final section id by taking some exceptions into account"
  [type-name-kw]
  (case type-name-kw
    :permit-description :description-of-permit
    type-name-kw))

(defn get-static-id
  "Some section types can only occur once and always have the same hardcoded id"
  [type]
  (some-> type static-types name keyword get-section-id))

(def top-level-types
  #{:verdict.section.type/template-page
    :verdict.section.type/cover-page
    :verdict.section.type/static-page
    :verdict.section.type/attachments
    :verdict.section.type/forms})

(def editable-section-types
  #{:verdict.section.type/template-page
    :verdict.section.type/cover-page})

(def copyable-section-types
  #{:verdict.section.type/template-page
    :verdict.section.type/cover-page})

(def sub-types-without-mandatory-identifier
  #{:verdict.section.type/section})

(def template-sub-types
  "Types that can be a direct child of HTML template"
  #{;; static content
    :verdict.section.type/bonds
    :verdict.section.type/inspections
    :verdict.section.type/general-conditions
    :verdict.section.type/permit-specific-conditions
    :verdict.section.type/permit-location
    :verdict.section.type/permit-base-data
    :verdict.section.type/roll-number
    :verdict.section.type/parties
    :verdict.section.type/permit-number
    :verdict.section.type/cover-page-permit-data
    :verdict.section.type/cover-page-reviews
    :verdict.section.type/table-of-contents
    :verdict.section.type/permit-category
    :verdict.section.type/permit-type
    :verdict.section.type/permit-set-label
    :verdict.section.type/permit-information
    :verdict.section.type/permit-description
    :verdict.section.type/qr-code
    :verdict.section.type/permission-granted
    :verdict.section.type/reviewers
    :verdict.section.type/permit-sign-off
    :verdict.section.type/form-field
    :verdict.section.type/authority-user
    :verdict.section.type/text-area-with-phrases
    :verdict.section.type/currency-input
    :verdict.section.type/number-input
    :verdict.section.type/date-input
    :verdict.section.type/property-attributes
    :verdict.section.type/header
    :verdict.section.type/work-type
    :verdict.section.type/date-applied
    :verdict.section.type/primary-address
    :verdict.section.type/municipality
    :verdict.section.type/subdivision
    :verdict.section.type/single-party
    :verdict.section.type/issue-date
    #__})

(def cover-page-only-types
  "Types that currently work on cover page only"
  #{:verdict.section.type/table-of-contents})

(defn disallowed-types [current-types top-level-section-type]
  (set/union
    (set/intersection current-types static-types)
    (case top-level-section-type
      :verdict.section.type/cover-page #{}
      cover-page-only-types)))

(def input-types
  "Sections that require user input. Used to separate different section types on front end."
  #{:verdict.section.type/permit-sign-off
    :verdict.section.type/authority-user
    :verdict.section.type/text-area-with-phrases
    :verdict.section.type/currency-input
    :verdict.section.type/number-input
    :verdict.section.type/date-input
    :verdict.section.type/single-party
    :verdict.section.type/issue-date
    #__})

(def editable-sub-types
  "Same as input-types, but missing permit-sign-off. Used to check which sections should have a required checkbox in
  verdict template creation."
  #{:verdict.section.type/authority-user
    :verdict.section.type/text-area-with-phrases
    :verdict.section.type/currency-input
    :verdict.section.type/number-input
    :verdict.section.type/date-input
    :verdict.section.type/issue-date
    #__})

(def deprecated-types
  "Section types that should not be added by the editor, but may
  be part of hardcoded default templates"
  #{;; We don't want to allow adding sections because they are somewhat redundant
    :verdict.section.type/section
    ;; Work description is redundant - use text-area-with-phrases
    :verdict.section.type/work-description
    ;; Only works with legacy fees, which exist nowhere
    :verdict.section.type/fees-and-payments})

(def sub-types-with-children
  #{:verdict.section.type/section
    :verdict.section.type/permit-information})

(def types-with-editable-children
  (set/union sub-types-with-children
             #{:verdict.section.type/template-page}))

(def static-sub-types
  (set/intersection template-sub-types static-types))

(def sub-types-with-css
  (set/union static-sub-types
             #{:verdict.section.type/single-party}))

(def types
  (concat top-level-types
          template-sub-types
          deprecated-types))

(def verdict-sections-with-defaults
  "Verdict section types which support default values"
  #{:verdict.section.type/authority-user
    :verdict.section.type/date-input})

(def input-types-without-defaults
  "Input section types that can never have default values configured"
  (set/difference input-types verdict-sections-with-defaults))

(def express-issuance-auto-filled-types
  "Input section types that need no configured default value because they are filled automatically
   on issuance. Issue date is set to the issuance date, so it makes no sense to have it in the defaults."
  #{:verdict.section.type/issue-date})

(def input-types-blocking-express-issuance
  "Input section types that make a verdict template unusable for express issuance: they require user input
   but can neither be configured as a default nor be filled automatically on issuance."
  (set/difference input-types-without-defaults express-issuance-auto-filled-types))


(def date-input-default-rules
  "Date-input verdict-section default value calculation rules"
  [;; add days to current date
   :verdict-default-config.date-input.rule/plus-days
   ;; end of next nth month or beginning of previous nth month (0 - end of current month)
   :verdict-default-config.date-input.rule/months
   ;; end of next nth year or beginning of previous nth year (0 - end of current year)
   :verdict-default-config.date-input.rule/end-of-year
   ;; specific day of year plus possible timewindow
   :verdict-default-config.date-input.rule/day-of-year])

(defn get-parent-template
  "Return the first found :entity.type/verdict-template parent"
  [section]
  (when-let [parent (:entity/parent section)]
    (if (eu/is-type? :entity.type/verdict-template parent)
      parent
      (get-parent-template parent))))

(defn get-label [tr section]
  (let [label  (:verdict.section/label section)
        tr-key (and (string? label)
                    (str/starts-with? label ":")
                    (-> label #?(:clj edn/decode :cljs cljs.reader/read-string)))]
    (if (keyword? tr-key)
      (tr [tr-key])
      label)))

(defmulti has-valid-value?
  "Check if verdict section is filled. Does not consider `:verdict.section/required`, check that
  separately if applicable."
  (fn [{:verdict.section/keys [type]}] type))

(defmethod has-valid-value? :default [{:verdict.section/keys [content]}]
  (-> content
      #?(:cljs identity :clj edn/decode)
      some?))

(defmethod has-valid-value? :verdict.section.type/date-input [{:verdict.section/keys [content]}]
  (-> content
      #?(:cljs identity :clj edn/decode)
      inst?))

(defmethod has-valid-value? :verdict.section.type/text-area-with-phrases [{:verdict.section/keys [content]}]
  (-> content
      #?(:cljs identity :clj edn/decode)
      rich-text/empty-html-rich-text?
      not))

(defn has-permit-sign-off?
  "Check if the given template has :verdict.section.type/permit-sign-off included,
  and if the %permit-sign-off% placeholder is also included in the html template.

  Note! This check does not guarantee that the permit can be really signed off,
  a more complex logic is required to determine that."
  [template]
  (let [sections (eu/descendants :entity.type/verdict-section template)]
    (boolean
      (and (->> sections
                (some #(= (:verdict.section/type %) :verdict.section.type/permit-sign-off)))
           (->> sections
                (keep :verdict.section/html-template)
                (some #(str/includes? % "%permit-sign-off%")))))))
